import api from './axiosConfig';

interface CreateBookingParams {
  guestId: string;
  botelId: string;
  checkIn: Date;
  checkOut: Date;
  totalAmount: number;
  specialRequests?: string;
  phone?: string;
  email?: string;
}

interface CreateGuestBookingParams {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  botelId: string;
  checkIn: Date;
  checkOut: Date;
  totalAmount: number;
  specialRequests?: string;
}

interface UpdatePaymentParams {
  bookingId: string;
  paymentIntentId: string;
  status: string;
}

export interface BookingDetails {
  id: string;
  guest_id: string;
  botel_id: string;
  botel: {
    id: string;
    name: string;
    location_address: string;
    mode?: string;
  };
  check_in: string;
  check_out: string;
  total_amount: number;
  status: string;
  payment_status: string;
  special_requests?: string;
  guest: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  created_at: string;
  updated_at: string;
  identity_verified: boolean;
}

export interface ExtendStayRequest {
  bookingId: string;
  extensionType: '4hours' | '8hours' | 'fullday';
}

export interface CancelBookingRequest {
  bookingId: string;
  reason?: string;
}

class BookingService {
  /**
   * Create a booking (authenticated endpoint)
   * Requires: Authorization: Bearer <token> header (automatically added by axiosConfig)
   * Backend will set created_by_user_id from the authenticated user's token
   */
  async createBooking(params: CreateBookingParams) {
    try {
      // api instance automatically adds Authorization: Bearer <token> header
      const response = await api.post('/bookings/create', params);
      return response.data;
    } catch (error) {
      console.error('Failed to create booking:', error);
      throw error;
    }
  }

  /**
   * Create a guest booking (authenticated endpoint)
   * Requires: Authorization: Bearer <token> header (automatically added by axiosConfig)
   * Backend will set created_by_user_id from the authenticated user's token
   */
  async createGuestBooking(params: CreateGuestBookingParams) {
    try {
      // Check if token exists before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in to create a booking.');
      }

      // api instance automatically adds Authorization: Bearer <token> header
      const response = await api.post('/bookings/guest/create', params);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create guest booking:', error);
      
      // Handle missing token
      if (error.message === 'Authentication required. Please log in to create a booking.') {
        throw error;
      }
      
      // Handle specific error for existing users
      if (error.response?.status === 409 && error.response?.data?.code === 'USER_EXISTS') {
        throw new Error(error.response.data.error || 'A user with this email or phone number already exists. Please log in to make a booking.');
      }
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in to create a booking.');
      }
      
      throw error;
    }
  }

  async updatePaymentStatus(params: UpdatePaymentParams) {
    try {
      const response = await api.post('/bookings/update-payment', params);
      return response.data;
    } catch (error) {
      console.error('Failed to update payment status:', error);
      throw error;
    }
  }

  async getBookingById(bookingId: string): Promise<BookingDetails> {
    const response = await api.get(`/bookings/${bookingId}`);
    return response.data;
  }

  async extendStay(request: ExtendStayRequest): Promise<BookingDetails> {
    const response = await api.post(`/bookings/${request.bookingId}/extend`, {
      extensionType: request.extensionType
    });
    return response.data;
  }

  async cancelBooking(request: CancelBookingRequest): Promise<BookingDetails> {
    const response = await api.post(`/bookings/${request.bookingId}/cancel`, {
      reason: request.reason
    });
    return response.data;
  }

  async addInsurance(bookingId: string): Promise<BookingDetails> {
    const response = await api.post(`/bookings/${bookingId}/insurance`);
    return response.data;
  }
}

export const bookingService = new BookingService(); 